import { Component, computed, effect, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { UiService } from '@core/ui/ui.service';
import { CompanySessionService } from '@core/auth/company-session.service';
import { OnboardingProgressService } from '@features/recruitment/dashboard/onboarding/onboarding-progress.service';
import { SidebarComponent } from '@core/layout/sidebar/sidebar.component';
import { TopbarComponent } from '@core/layout/topbar/topbar.component';
import { MobileHeaderComponent } from '@core/layout/mobile-header/mobile-header.component';
import { EMPLOYER_SIDEBAR } from '@core/layout/sidebar/sidebar.config';
import { environment } from '@env';
import { ConsentBannerComponent } from '@shared/components/consent-banner/consent-banner.component';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [SidebarComponent, TopbarComponent, MobileHeaderComponent, RouterOutlet, ConsentBannerComponent],
  template: `
    @if (ui.isDesktop()) {
      <div class="shell">
        <app-sidebar [config]="sidebarConfig" />
        <div class="shell-content">
          @if (showTopbar) {
            <app-topbar />
          }
          <main class="shell-main">
            <router-outlet />
          </main>
          @defer (on idle) {
            <app-consent-banner />
          }
        </div>
      </div>
    } @else {
      <div class="shell mobile">
        <app-mobile-header [showHamburger]="hasCompany()" (hamburgerClick)="sidebarOpen.set(true)" />
        <div class="shell-content">
          <main class="shell-main">
            <router-outlet />
          </main>
          @defer (on idle) {
            <app-consent-banner />
          }
        </div>
        <app-sidebar [config]="sidebarConfig" [open]="sidebarOpen()" (closed)="sidebarOpen.set(false)" />
      </div>
    }
  `,
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  showTopbar = false;
  ui = inject(UiService);
  private session = inject(CompanySessionService);
  private onboardingProgress = inject(OnboardingProgressService);
  sidebarOpen = signal(false);
  sidebarConfig = EMPLOYER_SIDEBAR;

  /**
   * Mirrors `recruitmentOnboardingGuard`: without a company the guard pins the user to
   * onboarding, so the sidebar has nowhere to go and the mobile hamburger is hidden.
   * Deliberately not the sidebar's `hasCompany` — that one shows items for any dev
   * build; here only the guard's own bypass flag may reveal the hamburger early.
   */
  hasCompany = computed(
    () => this.session.memberships().length > 0 || (environment.isDev && environment.devBypassOnboardedConstraint),
  );

  constructor() {
    // Resolve onboarding completion as soon as a company is active so the sidebar
    // can hide the Dashboard item app-wide, even when the user never lands on it.
    // ensureLoaded() is idempotent, so re-runs on company switch are cheap.
    effect(() => {
      if (this.session.activeCompany()) this.onboardingProgress.ensureLoaded().subscribe();
    });
  }
}
